/*
 * The first-run key card (chunk U-0's split of `panels.ts`).
 *
 * Deliberately the plainest thing on the screen. The phase's cut list keeps the
 * minimal-chrome policy, and this is one of only two pieces of interface a
 * playtester is given that are not the world talking to them — so it is sized
 * and placed to be findable and ignorable in that order.
 */

/**
 * The first-run card: the four keys, and nothing else.
 *
 * The roadmap's "no tutorial beyond keys", verbatim (Phase 4 decision 7). It
 * says how to move, how to look, how to talk and how to open the journal — the
 * whole verb list of the session — and then it goes on the first input and never
 * comes back. What it deliberately does *not* say is anything about the world:
 * whether there is a war, where to walk, or what the market board is for is the
 * experiment, and a card that answered any of that would answer the playtest's
 * questions before the playtest.
 *
 * It sits under the click-to-play overlay rather than inside it, because the two
 * have different lifetimes: the overlay comes back every time the pointer lock
 * is released and this is gone the moment a player has done anything at all.
 */
export class KeyCard {
  private readonly element: HTMLElement;
  private dismissed = false;

  constructor(root: HTMLElement, lines: readonly string[]) {
    this.element = document.createElement("div");
    // The kit's soft panel — glass and a hairline, no corner brackets — composed
    // alongside the client's own name (plan D2). A card is the client's own word
    // for this thing and a card has an edge; the blur the kit's panel ships with
    // is cancelled in `ui/hud.css` for every HUD line (plan D6).
    //
    // **The keys inside it are not chipped**, and that is a refusal recorded
    // here as well as in the stylesheet: `lines` is four sentences with the keys
    // already written into them (`main.ts`'s `KEY_CARD` and `TOUCH_CARD`), and
    // finding the letters again would be this file parsing prose somebody else
    // composed. The day `SessionChrome.keys` is a list of `{ press, does }` the
    // chips are three lines of markup.
    this.element.className = "hud__keys game-panel game-panel--soft";
    this.element.textContent = lines.join("\n");
    root.append(this.element);
  }

  get open(): boolean {
    return !this.dismissed;
  }

  /** Takes it away for the rest of the session. Idempotent. */
  dismiss(): void {
    if (this.dismissed) {
      return;
    }

    this.dismissed = true;
    this.element.hidden = true;
  }
}
